import { useState, useEffect, useMemo, useCallback } from 'react';
import { Calculator, Save, Pencil } from 'lucide-react';
import { Card, Panel, Divider, HStack } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon, Tooltip } from '@components/base/display';
import { Button } from '@components/base/actions';
import { TextField } from '@components/base/inputs';
import { Alert, Spinner, useToast } from '@components/base/feedback';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  SelectFieldOptions,
  SelectableOption,
} from '@components/integrated/entity-form';
import type { ColumnConfig } from '@components/integrated/data-list';
import {
  applyLayoutOverrides,
  applyColumnOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import { estimationSaveService } from '../api/estimationSaveService';
import type { VeosGuaranteeToClose } from '../api/taskService';
import { useClaimFlowAuthStore } from '../auth/store';
import type { EstimationRow } from '../types';
import {
  DEFAULT_DEDUCTION_POSTES,
  isBelowMinimum,
  savedPosteAmount,
  signedAmountString,
} from './deductionPostes';
import {
  COMMENT_GROUP_FIELDS,
  ESTIMATION_COMMENT_TEXT,
  FIELD_COMMENTAIRE,
  buildCommentaireFieldConfig,
  commentValue,
  requiredMessageOf,
  type EstimationSubmitError,
} from './estimationCommentaire';

// ===========================================================================
// Configurable-ready contract (rule-binding surface — DO NOT rename keys)
// ---------------------------------------------------------------------------
// Stable field keys (EntityForm config / `_layoutOverrides.fields`):
//   `garantie`     — the Garantie select
//   `perteTotale`  — the Perte totale checkbox
//   `commentaire`  — the required Commentaire textarea (mahhp 22858), rendered
//                    in its own field group under the net total; also the
//                    `commentaire` key of the WS2_GS_VER_ESTIMATION check
// Stable column keys (poste-list headers / `_layoutOverrides.columns`):
//   `poste`        — the poste label column
//   `montant`      — the editable amount column
//   `vrade`        — the Perte-totale-ON VRADE amount column
// Labels are display-only and overridable; the keys above are the contract a
// PM/admin rule targets. The card persists through its own workflow call (see
// the submit chain below), so the EntityForm config is driven in CONTROLLED
// mode and submitted by this card's own `handleSubmit` — the config
// `endpoint` is a formal placeholder and EntityForm's built-in submit is never
// used. The poste-list BODY (controlled inputs, MINIMUM validation, the live
// net total) stays imperative; only its HEADERS are declarative.
//
// Submit chain (ITEM-20261103-2139-xido): (a) the Commentaire must be filled
// (unless an admin hid the field) and the session must carry a `CD_UTIL` —
// checked before any call; (b) ONE call to the `claimflow-save-estimation`
// workflow (`estimationSaveService.save`) with every filled visible poste
// (signed as saved, `ecrire: true`), the Perte totale flag + VRADE amount, the
// comment and its author. The back end runs, in that one execution: the VEOS
// check `WS2_GS_VER_ESTIMATION` (fail closed — anything but `VERIF = OK` halts
// before any write; the total is computed server-side), the claim read +
// PERTETOTALE / MTVRADE PUT, one `sinistre-provision` per poste, then the
// comment (best-effort). The browser no longer calls any ERP write endpoint.
// ===========================================================================

// ---------------------------------------------------------------------------
// ERP proxy envelope types — mirror SettlementCard
// ---------------------------------------------------------------------------

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

type ErpListResponse<T> = ErpProxyResponse<{ total: number; data: T[] }>;

/**
 * Wrapper for the non-paginated `sinistre-inline-get` proxy call (the mount
 * read of the Perte totale default). Defensively handles both the bare flux
 * and the `{ total, data: [flux] }` list envelope, mirroring
 * `MiseAJourDossierCard`.
 */
interface SinistreFlux {
  infos?: Record<string, unknown>;
  [key: string]: unknown;
}

interface SinistreFluxResponse {
  success: boolean;
  data: SinistreFlux | { total: number; data: SinistreFlux[] };
  error?: { code: string; message: string } | null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EstimationCardProps {
  ses: string;
  idSin: string;
  /**
   * Claim reference (`NCBT_SIN`). **Currently unused** — kept as part of the
   * registered prop contract (`propsSchema`, passed by `TaskDetailPage`) so
   * existing rules and callers stay valid. It used to be forwarded as
   * `ncbt_sin` to `claimflow-post-comment` (mahhp 22858); since
   * ITEM-20261103-2139-xido the comment is posted by the
   * `claimflow-save-estimation` workflow, whose sealed input has no
   * `ncbt_sin` (it was only read for @mention tasks, which this card never
   * creates).
   */
  ncbtSin?: string;
  /**
   * CD_PSD codes whose amount is subtracted from the net total (Franchise,
   * Valeur de sauvetage). Their amount is forced NEGATIVE end to end — input,
   * display, save (mahhp 22851). Default {@link DEFAULT_DEDUCTION_POSTES}
   * (`FRAN`, `SAUV`, `ASSFRC`, `ERVSRC`); tenant-overridable via `setProp`.
   */
  deductionPostes?: string[];
  /**
   * Source of the Garantie select options (full replace). Passed from
   * `TaskDetailPage`'s `guaranteesToClose` state; mirrors
   * `CloseCoverageCard.guarantees`. Default []. The poste rows still come from
   * the estimations fetch (filtered by the selected CD_GAR).
   */
  guaranteesToClose?: VeosGuaranteeToClose[];
  onProvisionSaved?: () => void;
  /**
   * Incoming refresh trigger: when the parent bumps this value the estimations
   * fetch re-runs (e.g. after `OpenCoverageCard` opens new guarantees). This is
   * a parent-driven runtime control signal, NOT a tenant-configurable value, so
   * it is intentionally absent from the rule registry schema.
   */
  refreshKey?: number;
  /**
   * Per-tenant field/column layout overrides injected by the
   * `withComponentRules` HOC. Underscored to match the injected meta-prop
   * convention; `undefined` at baseline ⇒ the form config + poste columns are
   * returned unchanged (byte-identical render). `fields` merge into the
   * EntityForm config (`garantie`/`perteTotale`/`commentaire`); `columns`
   * merge into the poste column descriptor (`poste`/`montant`/`vrade`).
   */
  _layoutOverrides?: LayoutOverrides;
}

/** Per-row form state derived from an `EstimationRow`. */
interface PosteRow {
  row: EstimationRow;
  /**
   * Current input value (string). Typed amounts on a deduction poste are
   * already normalised to `-|n|`; a legacy positive stored amount is kept raw
   * here and signed at read time (display / modified test / total / save).
   */
  value: string;
  /**
   * Pre-filled value at load / after a successful save. "modified" = the
   * SIGNED value differs from the SIGNED baseline (so a legacy positive
   * deduction shown negative on load is not flagged).
   */
  baseline: string;
  /** Validation error (MINIMUM floor) or undefined. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Helpers — EUR_FORMATTER copied verbatim from SettlementCard
// ---------------------------------------------------------------------------

const EUR_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

/**
 * Unwrap the `sinistre-inline-get` proxy response. The endpoint is
 * non-paginated, so the flux lives at `response.data` — but defensively handle
 * the `{ total, data: [flux] }` envelope used by the list endpoints too.
 * Mirrors `MiseAJourDossierCard.unwrapFlux`.
 */
function unwrapFlux(payload: SinistreFluxResponse): SinistreFlux | null {
  const data: unknown = payload.data;
  if (typeof data !== 'object' || data === null) return null;
  const maybeEnvelope = data as { data?: unknown };
  if (Array.isArray(maybeEnvelope.data)) {
    const [first] = maybeEnvelope.data as SinistreFlux[];
    return first ?? null;
  }
  return data as SinistreFlux;
}

/** Treat null / undefined / '' as "no value present". */
function hasValue(v: string | number | null | undefined): boolean {
  return v !== null && v !== undefined && String(v) !== '';
}

/** The VRADE input as the workflow's `vrade` number — `null` when empty. */
function vradeAmount(value: string): number | null {
  if (!hasValue(value)) return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

/** Pre-fill precedence: MNT_SGD if present, else VALEUR_DEFAUT when proposed, else ''. */
function prefillValue(row: EstimationRow): string {
  if (hasValue(row.MNT_SGD)) return String(row.MNT_SGD);
  if (Number(row.DEFAUT_PROPOSE) === 1 && hasValue(row.VALEUR_DEFAUT)) {
    return String(row.VALEUR_DEFAUT);
  }
  return '';
}

function rowKey(row: EstimationRow): string {
  return `${row.CD_GAR}-${row.CD_PSD}`;
}

// ---------------------------------------------------------------------------
// Declarative config — fields (EntityForm) + poste-list column descriptor
// ---------------------------------------------------------------------------

const FIELD_GARANTIE = 'garantie';
const FIELD_PERTE_TOTALE = 'perteTotale';

const COL_POSTE = 'poste';
const COL_MONTANT = 'montant';
const COL_VRADE = 'vrade';

/**
 * Build the EntityForm config for the card's form controls: the Garantie
 * select + Perte totale checkbox (top group) and the Commentaire textarea
 * (bottom group, `COMMENT_GROUP_FIELDS`). The Garantie select's options are
 * runtime-derived from the estimations response and injected here (not a
 * config `optionsEndpoint`). `endpoint` is a formal placeholder — submit is
 * bespoke (see file header).
 */
function buildEstimationFormConfig(garantieItems: SelectableOption[]): EntityFormConfig {
  return {
    id: 'estimation-card',
    endpoint: {
      // Formal placeholder — EntityForm's built-in submit is never used; the
      // card persists through its own workflow call in `handleSubmit`.
      erpEndpoint: 'noop',
      method: 'POST',
    },
    fields: {
      [FIELD_GARANTIE]: {
        type: 'select',
        label: 'Garantie',
        order: 10,
        options: {
          items: garantieItems,
          placeholder: 'Sélectionner une garantie',
        } as SelectFieldOptions,
      },
      [FIELD_PERTE_TOTALE]: {
        type: 'checkbox',
        label: 'Perte totale',
        order: 20,
        options: { checkboxLabel: 'Perte totale' },
      },
      [FIELD_COMMENTAIRE]: buildCommentaireFieldConfig(30),
    },
    actions: {
      // The card renders its own bespoke submit button (workflow call in
      // `handleSubmit`); its label + visibility are sourced from this action so
      // the layout editor surfaces an `action` target. EntityForm's built-in
      // submit is never used (see file header).
      submit: { visible: true, label: 'Enregistrer la provision' },
      cancel: { visible: false },
      reset: { visible: false },
    },
    layout: { columns: 1 },
  };
}

/**
 * Poste-list column descriptors — stable keys are the contract; the French
 * headers are display-only / overridable. Declared as standalone constants
 * (never via `Record` indexing) so they double as the never-undefined fallback
 * the merged lookups resolve against. The `vrade` column covers the
 * Perte-totale-ON single-row surface.
 */
const POSTE_COLUMN_DEF: ColumnConfig = { label: 'Poste', order: 10, align: 'left' };
const MONTANT_COLUMN_DEF: ColumnConfig = { label: 'Montant', order: 20, align: 'right' };
const VRADE_COLUMN_DEF: ColumnConfig = { label: 'VRADE', order: 10, align: 'right' };

const posteColumns: Record<string, ColumnConfig> = {
  [COL_POSTE]: POSTE_COLUMN_DEF,
  [COL_MONTANT]: MONTANT_COLUMN_DEF,
};

const vradeColumns: Record<string, ColumnConfig> = {
  [COL_VRADE]: VRADE_COLUMN_DEF,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EstimationCard({
  ses,
  idSin,
  // `ncbtSin` is part of the registered props but no longer used (see its doc).
  deductionPostes = DEFAULT_DEDUCTION_POSTES,
  guaranteesToClose = [],
  onProvisionSaved,
  refreshKey,
  _layoutOverrides,
}: Readonly<EstimationCardProps>) {
  const { toast } = useToast();
  // The comment `user` (VEOS `CD_UTIL`), loaded at login (mahhp 22858).
  const cdUtil = useClaimFlowAuthStore((s) => s.cdUtil);

  // Collapsed on first paint — every card in the claim view's right column
  // opens folded.
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<EstimationSubmitError | null>(null);

  const [estimations, setEstimations] = useState<EstimationRow[]>([]);
  const [loading, setLoading] = useState(true);

  // `selectedGar` / `perteTotale` live in the EntityForm store (read back via
  // form.getValue below); the VRADE amount is local card state.
  const [vrade, setVrade] = useState('');

  // Default checked-state for the Perte totale checkbox: true only when the
  // claim flux (`sinistre-inline-get`) reports `infos.PERTETOTALE === 1`. This
  // is the SOURCE OF TRUTH for the total-loss flag — NOT the estimations VRADE
  // row (which only feeds the VRADE amount prefill). Drives the initial checked
  // state of the checkbox (see the init effect below).
  const [defaultPerteTotale, setDefaultPerteTotale] = useState(false);

  // Per-row state, keyed by CD_GAR-CD_PSD, for every estimation row (all
  // garanties). We slice the visible subset by selectedGar at render time.
  const [posteRows, setPosteRows] = useState<Record<string, PosteRow>>({});

  // Stable lookup of deduction codes.
  const deductionSet = useMemo(() => new Set(deductionPostes), [deductionPostes]);

  // -------------------------------------------------------------------------
  // Two independent load GETs, both guarded by a `cancelled` flag and re-run
  // whenever `ses`/`idSin`/`refreshKey` change (the parent bumps `refreshKey` to
  // force a reload after a dependent mutation, e.g. OpenCoverageCard opening
  // guarantees). StrictMode-safe: the second mount simply re-fetches, and any
  // superseded run is ignored via `cancelled` (mirror SettlementCard).
  //
  //  1. `boby-ws2-gs-tab-estimations-all` — the source of the POSTE ROWS
  //     (filtered by the selected CD_GAR) and the VRADE amount prefill. The
  //     Garantie select OPTIONS come from the `guaranteesToClose` prop instead
  //     (see `garantieItems` below).
  //  2. `sinistre-inline-get` — the claim flux, whose `infos.PERTETOTALE` flag
  //     is the SOURCE OF TRUTH for the Perte totale checkbox default. Kept a
  //     separate fetch so a flux-fetch failure never blanks the poste list, and
  //     the estimations VRADE row's own PERTETOTALE flag is intentionally NOT
  //     consulted for this (it only feeds the amount prefill above).
  // -------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function fetchEstimations() {
      try {
        const response = await claimflowApiClient.get<ErpListResponse<EstimationRow>>(
          '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-estimations-all',
          // perPage:1000 is MANDATORY — the default page size truncates the list.
          { params: { id_sin: idSin, perPage: 1000 } }
        );
        if (!cancelled && response.data.success) {
          const data = response.data.data.data;
          setEstimations(data);

          // Build per-row form state for every non-VRADE poste.
          const next: Record<string, PosteRow> = {};
          for (const row of data) {
            if (row.CD_GAR === 'VRADE') continue;
            const baseline = prefillValue(row);
            next[rowKey(row)] = { row, value: baseline, baseline, error: undefined };
          }
          setPosteRows(next);

          // VRADE pre-fill comes from the estimations VRADE row's MNT_SGD.
          const vradeRow = data.find((r) => r.CD_GAR === 'VRADE');
          const vradeInit = vradeRow && hasValue(vradeRow.MNT_SGD) ? String(vradeRow.MNT_SGD) : '';
          setVrade(vradeInit);
        }
      } catch {
        // silently fail — poste list stays empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    // Default the Perte totale checkbox from the claim flux's PERTETOTALE flag
    // (`sinistre-inline-get` → `infos.PERTETOTALE`), the source of truth. Checked
    // only when the flag is 1; the MTVRADE/MNT_SGD amount does NOT affect this —
    // a total loss with a null/0 amount still checks the box, and a non-zero
    // amount without the flag does not. (See the init effect below.) On any
    // failure the default stays false (unchecked) and the poste list is untouched.
    async function fetchPerteTotaleDefault() {
      try {
        const inline = await claimflowApiClient.get<SinistreFluxResponse>(
          '/claimflow/erp/registries/veos/endpoints/sinistre-inline-get',
          { params: { SES: ses, id_sin: idSin, inline: 1 } }
        );
        if (!cancelled && inline.data.success) {
          const flux = unwrapFlux(inline.data);
          setDefaultPerteTotale(Number(flux?.infos?.PERTETOTALE) === 1);
        }
      } catch {
        // silently fail — checkbox stays at its unchecked default
      }
    }

    void fetchEstimations();
    void fetchPerteTotaleDefault();

    return () => {
      cancelled = true;
    };
  }, [ses, idSin, refreshKey]);

  // -------------------------------------------------------------------------
  // Derived: selector options + visible rows (filtered to selectedGar, VRADE excluded)
  // -------------------------------------------------------------------------

  // Garantie options come from the `guaranteesToClose` prop (full replace —
  // the SOLE source of the select options). Dedupe by CD_GAR in
  // first-appearance order, keeping the first LIB_GAR seen for that code, and
  // defensively skip any VRADE entry. The poste rows are still sourced from the
  // estimations fetch (filtered by the selected CD_GAR), not from this prop.
  // Typed `SelectableOption[]` so it feeds the EntityForm select config.
  const garantieItems = useMemo<SelectableOption[]>(() => {
    const seen = new Set<string>();
    const items: SelectableOption[] = [];
    for (const gar of guaranteesToClose) {
      if (gar.CD_GAR === 'VRADE') continue;
      if (seen.has(gar.CD_GAR)) continue;
      seen.add(gar.CD_GAR);
      items.push({ value: gar.CD_GAR, label: gar.LIB_GAR });
    }
    return items;
  }, [guaranteesToClose]);

  // -------------------------------------------------------------------------
  // EntityForm (controlled): the form controls `garantie` + `perteTotale` (top
  // group) and `commentaire` (bottom group). Their VALUES live in the form
  // store; everything numeric/computed stays in this card's own state. Layout
  // overrides merge into the field config.
  // -------------------------------------------------------------------------

  const baseFormConfig = useMemo(() => buildEstimationFormConfig(garantieItems), [garantieItems]);

  usePublishLayoutConfig('claimflow.estimationCard', {
    ...buildPublishedConfigFromEntityForm(baseFormConfig, 'Nouvelle estimation'),
    columns: { ...posteColumns, ...vradeColumns },
  });

  const formConfig = useMemo(
    () => applyLayoutOverrides(baseFormConfig, _layoutOverrides),
    [baseFormConfig, _layoutOverrides]
  );

  const form = useEntityForm({ config: formConfig });

  // Read the two field values back from the form store (controlled mode).
  const selectedGarValue = form.getValue(FIELD_GARANTIE);
  const selectedGar = typeof selectedGarValue === 'string' ? selectedGarValue : '';
  const perteTotale = form.getValue(FIELD_PERTE_TOTALE) === true;

  // Two field groups over the SAME config + form store: the top group renders
  // every field but the comment group; the comment group renders under the
  // net total (mockup). Both stay `<EntityForm.Fields/>` auto-renders, so the
  // visibility / order / label overrides apply in each.
  const mainGroupFields = useMemo(
    () => Object.keys(formConfig.fields).filter((key) => !COMMENT_GROUP_FIELDS.includes(key)),
    [formConfig.fields]
  );
  const commentGroupVisible = COMMENT_GROUP_FIELDS.some((key) => form.isFieldVisible(key));

  // Resolved poste / VRADE column descriptors (effective label / hidden /
  // order / align / tooltip after the merge). Empty overrides ⇒ identity.
  const resolvedPosteColumns = useMemo(
    () => applyColumnOverrides(posteColumns, _layoutOverrides?.columns),
    [_layoutOverrides]
  );
  const resolvedVradeColumns = useMemo(
    () => applyColumnOverrides(vradeColumns, _layoutOverrides?.columns),
    [_layoutOverrides]
  );

  // Default the selected garantie to the first derived option once estimations
  // load. Guarded by `selectedGar === ''` so we never clobber a user's pick:
  // after the user selects another garantie, selectedGar is non-empty and this
  // effect is a no-op.
  useEffect(() => {
    const [first] = garantieItems;
    if (selectedGar === '' && first) {
      form.setValue(FIELD_GARANTIE, first.value);
    }
  }, [selectedGar, garantieItems, form]);

  // Initialise the Perte totale checkbox from the claim flux: checked when the
  // flux's `infos.PERTETOTALE` flag is 1, unchecked otherwise. Keyed on
  // `defaultPerteTotale` only (set by the fetch) so a later user toggle is never
  // clobbered — this re-runs solely when a fresh fetch flips the default, not on
  // every render (hence `form` is intentionally omitted from the deps; including
  // it would re-run on every render and clobber the user's toggle).
  useEffect(() => {
    form.setValue(FIELD_PERTE_TOTALE, defaultPerteTotale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPerteTotale]);

  const visibleRows = useMemo(
    () =>
      estimations
        .filter((r) => r.CD_GAR !== 'VRADE' && r.CD_GAR === selectedGar)
        .map((r) => posteRows[rowKey(r)])
        .filter((pr): pr is PosteRow => pr !== undefined),
    [estimations, selectedGar, posteRows]
  );

  // -------------------------------------------------------------------------
  // Net total: Σ(non-deduction) − Σ(|deduction|). VRADE excluded. Recomputed
  // sync. Deduction postes are subtracted on their MAGNITUDE, so the total is
  // right whether the amount reads `-1000` (forced sign) or a legacy `1000`.
  // -------------------------------------------------------------------------

  const netTotal = useMemo(
    () =>
      visibleRows.reduce((acc, pr) => {
        const amount = Number.parseFloat(pr.value) || 0;
        return deductionSet.has(pr.row.CD_PSD) ? acc - Math.abs(amount) : acc + amount;
      }, 0),
    [visibleRows, deductionSet]
  );

  // -------------------------------------------------------------------------
  // Row editing + validation (MINIMUM floor)
  //
  // A deduction poste's amount is forced NEGATIVE as the user types (mahhp
  // 22851): a non-zero value becomes `-|n|` (typing `1000` shows `-1000`,
  // deleting the sign puts it back), while `''`, `0` and partial input stay as
  // typed. The same call runs on blur to normalise whatever the field holds.
  // Its MINIMUM floor is checked on the magnitude.
  // -------------------------------------------------------------------------

  const updateAmount = useCallback(
    (key: string, rawValue: string) => {
      setPosteRows((prev) => {
        const pr = prev[key];
        if (!pr) return prev;
        const isDeduction = deductionSet.has(pr.row.CD_PSD);
        const value = signedAmountString(rawValue, isDeduction);
        let error: string | undefined;
        const minimum = pr.row.MINIMUM;
        if (minimum !== null) {
          const min = Number(minimum);
          if (!Number.isNaN(min) && isBelowMinimum(value, min, isDeduction)) {
            error = `Minimum ${String(minimum)} € déjà réglé`;
          }
        }
        if (value === pr.value && error === pr.error) return prev;
        return { ...prev, [key]: { ...pr, value, error } };
      });
    },
    [deductionSet]
  );

  const hasVisibleError = useMemo(() => visibleRows.some((pr) => Boolean(pr.error)), [visibleRows]);

  // Savable-amount gate: submit is enabled whenever there is something worth
  // persisting — at least one visible poste carries a non-empty amount, or (in
  // Perte totale mode) the VRADE field has a value. The pre-filled estimation
  // can therefore be saved as-is, without the user having to edit a row first.
  const hasSavableAmount =
    visibleRows.some((pr) => hasValue(pr.value)) || (perteTotale && hasValue(vrade));

  const submitDisabled = submitting || !hasSavableAmount || hasVisibleError;

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    // (a) Commentaire gate (mahhp 22858) — checked before ANY call. Config
    // driven: a field an admin hid (`hideField`, or a `setFieldCondition`
    // hide) is neither required nor posted, but the save (and its VEOS check,
    // with `commentaire = ''`) still runs.
    const commentVisible = form.isFieldVisible(FIELD_COMMENTAIRE);
    const comment = commentVisible ? commentValue(form.getValue(FIELD_COMMENTAIRE)) : '';
    if (commentVisible && form.isFieldRequired(FIELD_COMMENTAIRE) && comment === '') {
      form.setError(FIELD_COMMENTAIRE, requiredMessageOf(formConfig.fields[FIELD_COMMENTAIRE]));
      return;
    }
    // The comment `user` is the session's CD_UTIL — without it nothing is sent.
    let commentCreator: string | null = null;
    if (comment !== '') {
      if (!cdUtil) {
        setSubmitError({ message: ESTIMATION_COMMENT_TEXT.missingCdUtil });
        return;
      }
      commentCreator = cdUtil;
    }
    if (commentVisible) form.clearError(FIELD_COMMENTAIRE);

    setSubmitting(true);

    // Persist every visible poste that carries a non-empty amount (not just the
    // edited ones) so a pre-filled estimation is saved as-is. Snapshotted here so
    // the payload and the baseline reset agree on the exact set of rows.
    const filledVisibleRows = visibleRows.filter((pr) => hasValue(pr.value));

    try {
      // (b) ONE call: the `claimflow-save-estimation` workflow checks the
      // estimation with VEOS (fail closed) and, only on `VERIF = OK`, writes
      // it (PERTETOTALE / MTVRADE PUT + one provision per poste) and posts the
      // comment — server-side. Each amount is SIGNED as saved: a deduction
      // poste NEGATIVE (VEOS only deducts it with the sign), guarded here too
      // for a legacy positive row. No total is sent: the server computes it.
      const result = await estimationSaveService.save({
        estimation: 'reglement',
        idSin,
        garantie: selectedGar,
        postes: filledVisibleRows.map((pr) => ({
          code: pr.row.CD_PSD,
          mnt: savedPosteAmount(pr.value, deductionSet.has(pr.row.CD_PSD)),
          ecrire: true,
        })),
        perteTotale,
        vrade: perteTotale ? vradeAmount(vrade) : null,
        commentaire: comment,
        creator: commentCreator,
      });
      if (!result.ok) {
        // A blocked VEOS check carries the title « Contrôle VEOS » (the
        // message is VEOS', not an echo of the user's comment); an input /
        // save error none. The comment stays in the field for a retry.
        setSubmitError({ title: result.title, message: result.message });
        return;
      }

      // Saved: reset each posted row's baseline to its saved value so the
      // modified-row Pencil highlight clears for everything we just persisted.
      setPosteRows((prev) => {
        const next = { ...prev };
        for (const pr of filledVisibleRows) {
          next[rowKey(pr.row)] = { ...pr, baseline: pr.value };
        }
        return next;
      });

      // (c) The provision is saved but the comment step failed (best-effort
      // on the back end): still refresh the parent and keep the text in the
      // field (to be copied into the Commentaires panel) — never silent.
      if (result.commentError !== null) {
        onProvisionSaved?.();
        setSubmitError({
          message: ESTIMATION_COMMENT_TEXT.commentNotSent(
            'La provision a été enregistrée',
            result.commentError
          ),
        });
        return;
      }

      // (d) Success as before, plus a fresh (empty) comment for the next one.
      toast({ variant: 'success', title: 'Provision enregistrée' });
      onProvisionSaved?.();
      setExpanded(false);
      if (commentVisible) form.setValue(FIELD_COMMENTAIRE, '');
    } finally {
      setSubmitting(false);
    }
  }, [
    form,
    formConfig.fields,
    cdUtil,
    perteTotale,
    idSin,
    selectedGar,
    vrade,
    visibleRows,
    deductionSet,
    toast,
    onProvisionSaved,
  ]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // The merge never drops keys, so these always resolve; the `?? def` keeps
  // the indexed-access type non-undefined without changing runtime behaviour.
  const posteCol = resolvedPosteColumns[COL_POSTE] ?? POSTE_COLUMN_DEF;
  const montantCol = resolvedPosteColumns[COL_MONTANT] ?? MONTANT_COLUMN_DEF;
  const vradeCol = resolvedVradeColumns[COL_VRADE] ?? VRADE_COLUMN_DEF;

  return (
    <Card id={actionAnchorId('claimflow.estimationCard')}>
      <Card.Header collapsible defaultExpanded={expanded} onExpandChange={setExpanded}>
        <Icon icon={Calculator} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Nouvelle estimation', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Fields className="mb-4" exclude={COMMENT_GROUP_FIELDS} />
        </EntityForm>

        <>
          {/* VRADE (Perte totale) field — shown when the box is checked,
              ALONGSIDE the poste list. Toggling the box no longer hides the
              other estimations; VRADE stays excluded from the net total. */}
          {perteTotale && !vradeCol.hidden && (
            <div className="mb-3">
              <Panel variant="outlined" padding="default">
                <HStack justify="between" align="center">
                  <ColumnHeaderLabel column={vradeCol} />
                  <div className="w-40">
                    <TextField
                      type="number"
                      size="sm"
                      value={vrade}
                      onChange={(e) => {
                        setVrade(e.target.value);
                      }}
                      // "0.00" is a numeric input FORMAT mask (an example of the
                      // expected decimal shape), not a tenant-facing label — it
                      // has no config slot and is intentionally left literal.
                      placeholder="0.00"
                      className="text-right"
                      // Accessible name follows the merged VRADE column label so
                      // a `renameColumn` rule on `vrade` renames this input too.
                      aria-label={vradeCol.label}
                    />
                  </div>
                </HStack>
              </Panel>
            </div>
          )}

          {/* Poste list — always visible (no longer hidden by the Perte totale
              toggle). Bordered container, one row per poste, no header row. */}
          <Panel variant="outlined" padding="default">
            {loading ? (
              <div className="flex items-center justify-center min-h-32">
                <Spinner size="md" />
              </div>
            ) : visibleRows.length === 0 ? (
              <Text size="sm" color="muted">
                Aucun poste pour cette garantie
              </Text>
            ) : (
              visibleRows.map((pr, idx) => {
                const isDeduction = deductionSet.has(pr.row.CD_PSD);
                const label = isDeduction ? `${pr.row.LIB_PSD} (−)` : pr.row.LIB_PSD;
                // Deduction postes always display negative — including a
                // legacy positive stored amount (mahhp 22851).
                const displayValue = signedAmountString(pr.value, isDeduction);
                // A poste is "modified" when its value differs from its
                // baseline (both signed, so a freshly loaded legacy deduction
                // is not flagged). Reuse RequestPiecesCard's highlight
                // treatment: primary-tinted wrapper + a Pencil indicator.
                const isModified =
                  displayValue !== signedAmountString(pr.baseline, isDeduction);
                const wrapperClass = isModified
                  ? 'rounded-md border border-primary-200 bg-primary-50/40 px-2 py-1'
                  : 'px-2 py-1';
                return (
                  <div key={rowKey(pr.row)}>
                    {idx > 0 && <Divider spacing={2} />}
                    <div className={wrapperClass}>
                      <HStack justify="between" align="center">
                        {posteCol.hidden ? null : (
                          <HStack gap={2} align="center">
                            {isModified && (
                              <span data-testid={`poste-modified-${pr.row.CD_PSD}`}>
                                <Icon icon={Pencil} size="sm" color="primary" />
                              </span>
                            )}
                            <PosteLabel column={posteCol} text={label} isModified={isModified} />
                          </HStack>
                        )}
                        {montantCol.hidden ? null : (
                          <div className="w-40">
                            <TextField
                              type="number"
                              size="sm"
                              value={displayValue}
                              onChange={(e) => {
                                updateAmount(rowKey(pr.row), e.target.value);
                              }}
                              // Deduction postes re-normalise on blur (force the
                              // sign on whatever the field holds); other postes
                              // keep their change-only behaviour.
                              onBlur={
                                isDeduction
                                  ? (e) => {
                                      updateAmount(rowKey(pr.row), e.target.value);
                                    }
                                  : undefined
                              }
                              // Numeric input FORMAT mask, not a label — see the
                              // VRADE input above; intentionally left literal.
                              placeholder="0.00"
                              className="text-right"
                              error={Boolean(pr.error)}
                              errorMessage={pr.error}
                              aria-label={`${montantCol.label} pour ${pr.row.LIB_PSD}`}
                            />
                          </div>
                        )}
                      </HStack>
                    </div>
                  </div>
                );
              })
            )}
          </Panel>

          {/* Provision nette estimée — subtle grey row beneath the list. */}
          <div className="mt-3 flex items-center justify-between rounded-md bg-gray-50 px-4 py-3">
            <Text size="sm" color="muted">
              Provision nette estimée
            </Text>
            <Text size="base" weight="bold" color="primary">
              {EUR_FORMATTER.format(netTotal)}
            </Text>
          </div>
        </>

        {/* Comment group (mahhp 22858) — the config-driven Commentaire field,
            under the net total and above the submit button. A second
            EntityForm root over the SAME config + form store (not a separate
            form), so values, errors and layout overrides are shared. */}
        {commentGroupVisible && (
          <EntityForm config={formConfig} form={form} className="mt-4">
            <EntityForm.Fields exclude={mainGroupFields} />
          </EntityForm>
        )}

        {/* Submit error — right above the submit button (not at the top of
            the card, out of sight after a click). A blocked VEOS check (KO or
            unavailable) carries the title « Contrôle VEOS »; the other errors
            none. */}
        {submitError && (
          <Alert variant="error" title={submitError.title} className="mt-4">
            {submitError.message}
          </Alert>
        )}

        {formConfig.actions?.submit?.visible !== false && (
          <div className="pt-4">
            <Button
              type="button"
              variant="primary"
              leftIcon={<Icon icon={Save} size="sm" />}
              loading={submitting}
              disabled={submitDisabled}
              onClick={handleSubmit}
              className="w-full"
            >
              {formConfig.actions?.submit?.label ?? 'Enregistrer la provision'}
            </Button>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Imperative column-header renderers (read the resolved descriptor)
// ---------------------------------------------------------------------------

/**
 * Renders a poste row's label cell. The header text is the resolved column's
 * effective `label`-driven cell content; the deduction " (−)" marker is
 * data-derived and already folded into `text`. A `headerTooltip` (if set by a
 * rule) reveals on hover via the shared {@link Tooltip}.
 */
function PosteLabel({
  column,
  text,
  isModified = false,
}: Readonly<{ column: ColumnConfig; text: string; isModified?: boolean }>) {
  const label = (
    <Text size="sm" color={isModified ? 'primary' : 'default'}>
      {text}
    </Text>
  );
  if (column.headerTooltip) {
    return <Tooltip content={column.headerTooltip}>{label}</Tooltip>;
  }
  return label;
}

/**
 * Renders the resolved label for the VRADE column header (the left cell of the
 * Perte-totale-ON single row). A `headerTooltip` reveals on hover.
 */
function ColumnHeaderLabel({ column }: Readonly<{ column: ColumnConfig }>) {
  const label = <Text size="sm">{column.label}</Text>;
  if (column.headerTooltip) {
    return <Tooltip content={column.headerTooltip}>{label}</Tooltip>;
  }
  return label;
}
